import { ProductKeys3P } from '../../../common/ui/configs/types';

/**
 * @internal Implementation detail of `useRovoConfigStore`. Do **not** import
 * this from anywhere other than `src/services/hooks/useRovoConfigStore.tsx` —
 * it is intentionally not part of the package's public API.
 *
 * The set of third-party connector keys that the chat config endpoint can
 * surface in `thirdPartyConfigs` but which the chat surface does **not** yet
 * support, so they must not be projected into the store.
 *
 * This is the 3P counterpart to the 1P exclusion in
 * {@link isRecognisedFirstPartyConnector}: rather than allow-listing the keys
 * we recognise (as the 1P loop does, because `accessibleProducts` is broad and
 * noisy), the 3P config endpoint only ever returns genuine 3P connectors, so
 * we instead deny-list the specific keys that are not ready. Everything else
 * is supported by default — mirroring how `isRecognisedFirstPartyConnector`
 * stays in sync with new products via the canonical constants.
 *
 * TODO RAOC-23: `jsm-dc` (Jira Service Management Data Center) is a genuine 3P
 * connector but is not yet supported on the chat surface. Remove it from this
 * set once chat support lands.
 */
const THIRD_PARTY_KEYS_PENDING_SUPPORT: ReadonlySet<string> = new Set<string>([
	ProductKeys3P.JsmDC,
]);

/**
 * @internal Only for use by `useRovoConfigStore`.
 *
 * Returns `true` when the given third-party config-namespace product key is a
 * connector that should be synthesised into the store. Returns `false` for
 * connectors that are not yet supported on the chat surface (see
 * {@link THIRD_PARTY_KEYS_PENDING_SUPPORT}).
 */
export const isSupportedThirdPartyConnector = (productKey: string): boolean =>
	!THIRD_PARTY_KEYS_PENDING_SUPPORT.has(productKey);
